import type { PaginatedList, Patrol, Vehicle } from '@local/common-lib';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthenticated } from '../utils/auth/index.js';

export interface CombinedVehicle extends Patrol.PatrolVehicleInfo {
  fromRegister?: boolean;
}

export const useVehicleRegister = (
  recentVehicles: Patrol.PatrolVehicleInfo[],
): [CombinedVehicle[], boolean, () => Promise<void>] => {
  const auth = useAuthenticated();
  const [registeredVehicles, setRegisteredVehicles] = useState<Vehicle.Type[]>(
    [],
  );
  const [loading, setLoading] = useState(true);

  const fetchVehicles = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await auth.axios.get<PaginatedList<Vehicle.Type>>(
        '/api/v1/my/organization/vehicles',
      );
      setRegisteredVehicles(data.items);
    } catch (error) {
      console.error('Error loading vehicle register', error);
      setRegisteredVehicles([]);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  const combined = useMemo(() => {
    // Convert registered vehicles to the combined format
    const registeredMap = new Map<string, CombinedVehicle>(
      registeredVehicles.map((v) => [
        v.rego,
        {
          color: v.color,
          fromRegister: true,
          hasLivery: v.hasLivery,
          make: v.make,
          model: v.model,
          rego: v.rego,
        },
      ]),
    );

    // Add recent vehicles that aren't in the register
    recentVehicles.forEach((v) => {
      if (!registeredMap.has(v.rego)) {
        registeredMap.set(v.rego, {
          ...v,
          fromRegister: false,
        });
      }
    });

    // Sort by rego
    return Array.from(registeredMap.values()).sort((a, b) =>
      a.rego.localeCompare(b.rego),
    );
  }, [registeredVehicles, recentVehicles]);

  return [combined, loading, fetchVehicles];
};
